import { useQuery } from '@tanstack/react-query';

import { fetchEmployeeSession, refreshEmployeeSession } from '../api/session';
import { resolveSession } from '../model/refresh-policy';

import type { EmployeeSession } from '../api/session';

export const employeeSessionQueryKey = ['employee-session'] as const;

/** Without an expiry (no session) the answer is re-read on this cadence. */
const NO_SESSION_STALE_MS = 5 * 60_000;

/** Fresh until the access token expires; then the next read refreshes it. */
export function employeeSessionStaleTime(
    session: EmployeeSession | undefined,
    now: number,
): number {
    const expiresAt = session?.expiresAt ? Date.parse(session.expiresAt) : Number.NaN;
    return Number.isNaN(expiresAt) ? NO_SESSION_STALE_MS : Math.max(0, expiresAt - now);
}

/**
 * The employee-pool session: its permissions, for the nav, and its expiry.
 * An expired one with a live refresh cookie is refreshed once on the way.
 */
export function useEmployeeSession() {
    return useQuery({
        queryKey: employeeSessionQueryKey,
        queryFn: ({ signal }) =>
            resolveSession(() => fetchEmployeeSession(signal), refreshEmployeeSession),
        staleTime: (query) => employeeSessionStaleTime(query.state.data, Date.now()),
        retry: 1,
        networkMode: 'always',
    });
}
